import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "@/services/api";

/*
|--------------------------------------------------------------------------
| Appointment Listing
|--------------------------------------------------------------------------
*/

export const getAppointments = createAsyncThunk(
    "appointment/getAppointments",
    async (params, { rejectWithValue }) => {
        try {
            const response = await api.get(
                `admin/appointments?tab=${params.tab || ""}&search=${params.search || ""}&date=${params.date || ""}&sales_rep_id=${params.sales_rep_id || ""}&sort_by=${params.sort_by || "appointment_date"}&sort_order=${params.sort_order || "desc"}&page=${params.page || 1}`
            );

            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to fetch appointments."
            );
        }
    }
);

/*
|--------------------------------------------------------------------------
| Appointment Details
|--------------------------------------------------------------------------
*/

export const getAppointmentDetails = createAsyncThunk(
    "appointment/getAppointmentDetails",
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(
                `admin/appointments/${id}`
            );

            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to fetch appointment details."
            );
        }
    }
);

/*
|--------------------------------------------------------------------------
| Approve Appointment
|--------------------------------------------------------------------------
*/

export const approveAppointment = createAsyncThunk(
    "appointment/approveAppointment",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(
                `admin/appointments/${data?.id}/approve`,
                data
            );

            return response;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to approve appointment."
            );
        }
    }
);

/*
|--------------------------------------------------------------------------
| Reject Appointment
|--------------------------------------------------------------------------
*/

export const rejectAppointment = createAsyncThunk(
    "appointment/rejectAppointment",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(
                `admin/appointments/${data?.id}/reject`,
                data
            );

            return response;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to reject appointment."
            );
        }
    }
);

/*
|--------------------------------------------------------------------------
| Assign Sales Representative
|--------------------------------------------------------------------------
*/

export const assignAppointment = createAsyncThunk(
    "appointment/assignAppointment",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(
                `admin/appointments/${data?.id}/assign`,
                data
            );

            return response;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to assign appointment."
            );
        }
    }
);


export const assignAppointmentToSalesTeam = createAsyncThunk(
    "appointment/assignAppointmentToSalesTeam",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(
                `admin/appointments/${data?.id}/assign-to-member`,
                data
            );

            return response;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to assign appointment."
            );
        }
    }
);

export const getSalesManagers = createAsyncThunk(
    "appointment/getSalesManagers",
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get(`admin/get-sales-managers`);
            const data = response.data.map(row => ({
                value: row.id, label: row.first_name, offices: (row.offices || []).map((office) => ({
                    value: office.id,
                    label: office.office_name,
                })),
            }));
            return data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to fetch sales managers."
            );
        }
    }
);

export const getSalesMembers = createAsyncThunk(
    "appointment/getSalesMembers",
    async (formdata, { rejectWithValue }) => {
        try {
            const response = await api.get(`admin/office-members?user_id=${formdata.manager_id}`);
            // const data = response.data.map(row => ({ value: row.user_id, label: row.full_name }));
            const list = Array.isArray(response.data) ? response.data : (response.data?.data || []);
            const data = list.map(row => ({ value: row.user_id, label: row.full_name }));
            return data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to fetch sales members."
            );
        }
    }
);

export const getNotificationLogs = createAsyncThunk(
    "appointment/getNotificationLogs",
    async (appointment_id, { rejectWithValue }) => {
        try {
            const response = await api.get(`admin/appointments/${appointment_id}/notifications`);
            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to fetch logs."
            );
        }
    }
);

/*
|--------------------------------------------------------------------------
| Mark Customer Contacted
|--------------------------------------------------------------------------
*/

export const markCustomerContacted = createAsyncThunk(
    "appointment/markCustomerContacted",
    async (appointment_id, { rejectWithValue }) => {
        try {
            const response = await api.post(
                `admin/appointments/${appointment_id}/contacted`
            );

            return response;
        } catch (err) {
            return rejectWithValue(
                err.response?.data ||
                "Failed to mark customer contacted."
            );
        }
    }
);

/*
|--------------------------------------------------------------------------
| Reschedule Appointment
|--------------------------------------------------------------------------
*/

export const rescheduleAppointment = createAsyncThunk(
    "appointment/rescheduleAppointment",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(
                `admin/appointments/${data.id}/reschedule`,
                data
            );

            return response;
        } catch (err) {
            return rejectWithValue(
                err.response?.data ||
                "Failed to reschedule appointment."
            );
        }
    }
);

/*
|--------------------------------------------------------------------------
| Cancel Appointment
|--------------------------------------------------------------------------
*/

export const cancelAppointment = createAsyncThunk(
    "appointment/cancelAppointment",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(
                `admin/appointments/${data?.id}/cancel`,
                data
            );

            return response;
        } catch (err) {
            return rejectWithValue(
                err.response?.data ||
                "Failed to cancel appointment."
            );
        }
    }
);

/*
|--------------------------------------------------------------------------
| Notifications
|--------------------------------------------------------------------------
*/

export const getAppointmentNotifications =
    createAsyncThunk(
        "appointment/getAppointmentNotifications",
        async (id, { rejectWithValue }) => {
            try {
                const response = await api.get(
                    `admin/appointments/${id}/notifications`
                );

                return response.data;
            } catch (err) {
                return rejectWithValue(
                    err.response?.data ||
                    "Failed to fetch notifications."
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| Calendar
|--------------------------------------------------------------------------
*/

export const getAppointmentCalendar =
    createAsyncThunk(
        "appointment/getAppointmentCalendar",
        async (_, { rejectWithValue }) => {
            try {
                const response = await api.get(
                    `admin/appointments/calendar`
                );

                return response.data;
            } catch (err) {
                return rejectWithValue(
                    err.response?.data ||
                    "Failed to fetch calendar."
                );
            }
        }
    );

/*
|--------------------------------------------------------------------------
| Statistics
|--------------------------------------------------------------------------
*/

export const getAppointmentStatistics =
    createAsyncThunk(
        "appointment/getAppointmentStatistics",
        async (_, { rejectWithValue }) => {
            try {
                const response = await api.get(
                    `admin/appointments/statistics`
                );

                return response.data;
            } catch (err) {
                return rejectWithValue(
                    err.response?.data ||
                    "Failed to fetch statistics."
                );
            }
        }
    );

export const completeAppointment = createAsyncThunk(
    "appointment/completeAppointment",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(
                `admin/appointments/${data?.id}/complete`,
                data
            );

            return response;
        } catch (err) {
            return rejectWithValue(
                err.response?.data ||
                "Failed to complete appointment."
            );
        }
    }
);


export const getAvailableSlots = createAsyncThunk(
    "appointment/getAvailableSlots",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.get(
                `customer/appointments/available-slots?date=${data?.appointment_date}`
            );

            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to fetch available slots."
            );
        }
    }
);

/*
|--------------------------------------------------------------------------
| Appointment Customers (search + pagination)
|--------------------------------------------------------------------------
*/
export const getAppointmentCustomers = createAsyncThunk(
    "appointment/getAppointmentCustomers",
    async (params, { rejectWithValue, signal }) => {
        try {
            const { search = '', page = 1, per_page = 5 } = params;

            const response = await api.get(
                'admin/appointment-customers?search=' + encodeURIComponent(search) + '&page=' + page + '&per_page=' + per_page,
            );

            return response.data;
        } catch (err) {
            if (err.name === "CanceledError" || err.code === "ERR_CANCELED") {
                throw err;
            }

            return rejectWithValue(
                err.response?.data || { message: "Failed to fetch customers." }
            );
        }
    }
);

/*
|--------------------------------------------------------------------------
| Appointment Subjects (admin dropdown)
|--------------------------------------------------------------------------
*/
export const getAppointmentSubjectsAdmin = createAsyncThunk(
    "appointment/getAppointmentSubjectsAdmin",
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get(`admin/get-appontment-subjects-admin`);
            return response.data || [];
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to fetch subjects."
            );
        }
    }
);


export const createAppointmentAdmin = createAsyncThunk(
    "appointment/createAppointmentAdmin",
    async (data, { rejectWithValue }) => {
        try {
            const response = await api.post(
                `admin/appointments-for-customer`,
                data
            );

            return response.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || "Failed to create appointment."
            );
        }
    }
);


const appointmentSlice = createSlice({
    name: "appointment",

    initialState: {
        appointments: [],
        appointmentDetails: null,
        tab_counts: {},
        notifications: [],
        calendar: [],
        statistics: {},
        salesManagers: [],
        salesMembers: [],
        notificationLogs: [],
        loading: false,
        list_loading: false,
        selected: null,
        availableSlots: [],
        customers: { data: [], current_page: 1, last_page: 1, next_page_url: null, total: 0 },
        customersLoading: false,
        customersError: null,
        subjectsAdmin: [],
    },

    reducers: {
        setSelectedAppointment(state, action) {
            state.selected = action.payload;
        },

        unsetSelectedAppointment(state) {
            state.selected = null;
        },

        resetStatus(state) {
            state.loading = false;
            state.list_loading = false;
        },
    },

    extraReducers: (builder) => {
        builder

            // List
            .addCase(getAppointments.pending, (state) => {
                state.list_loading = true;
            })
            .addCase(getAppointments.fulfilled, (state, action) => {
                state.list_loading = false;
                const payload = action.payload;
                state.tab_counts = payload?.tab_counts || {};

                const currentPage =
                    action.payload?.data?.appointments?.current_page || 1;

                if (currentPage === 1) {
                    state.appointments = action.payload.data;
                } else {
                    state.appointments = {
                        ...action.payload.data,
                        appointments: {
                            ...action.payload.data.appointments,
                            data: [
                                ...(state.appointments?.appointments?.data || []),
                                ...(action.payload.data.appointments?.data || []),
                            ],
                        },
                    };
                }
            })
            .addCase(getAppointments.rejected, (state) => {
                state.list_loading = false;
            })

            // Details
            .addCase(
                getAppointmentDetails.fulfilled,
                (state, action) => {
                    state.appointmentDetails =
                        action.payload;
                }
            )

            // Notifications
            .addCase(
                getAppointmentNotifications.fulfilled,
                (state, action) => {
                    state.notifications =
                        action.payload;
                }
            )

            // Calendar
            .addCase(
                getAppointmentCalendar.fulfilled,
                (state, action) => {
                    state.calendar = action.payload;
                }
            )

            // Statistics
            .addCase(
                getAppointmentStatistics.fulfilled,
                (state, action) => {
                    state.statistics = action.payload;
                }
            )

            // Sales Managers
            .addCase(
                getSalesManagers.fulfilled,
                (state, action) => {
                    state.salesManagers = action.payload;
                }
            )
            // Sales Members
            .addCase(
                getSalesMembers.fulfilled,
                (state, action) => {
                    state.salesMembers = action.payload;
                }
            )
            // Sales Members
            .addCase(
                getNotificationLogs.fulfilled,
                (state, action) => {
                    state.notificationLogs = action.payload;
                }
            )
            .addCase(
                getAvailableSlots.pending,
                (state, action) => {
                    state.loading = true;
                }
            )
            .addCase(
                getAvailableSlots.fulfilled,
                (state, action) => {
                    state.availableSlots = action.payload;
                    state.loading = false;
                }
            )
            .addCase(
                getAvailableSlots.rejected,
                (state, action) => {
                    state.loading = false;
                }
            )
            .addCase(getAppointmentCustomers.pending, (state) => {
                state.customersLoading = true;
                state.customersError = null;
            })
            .addCase(getAppointmentCustomers.fulfilled, (state, action) => {
                state.customersLoading = false;
                state.customersError = null;

                const payload = action.payload;

                if (payload.current_page === 1) {
                    state.customers = payload;
                } else {
                    state.customers = {
                        ...payload,
                        data: [
                            ...(state.customers?.data || []),
                            ...payload.data,
                        ],
                    };
                }
            })

            .addCase(getAppointmentCustomers.rejected, (state, action) => {
                state.customersLoading = false;
                if (action.meta?.aborted) return;
                state.customersError = action.payload?.message || "Failed to fetch customers.";
            })
            .addCase(getAppointmentSubjectsAdmin.fulfilled, (state, action) => {
                state.subjectsAdmin = action.payload;
            })


            // Global loading actions
            .addMatcher(
                (action) =>
                    action.type.startsWith("appointment/") &&
                    action.type.endsWith("/pending"),
                (state) => {
                    state.loading = true;
                }
            )

            .addMatcher(
                (action) =>
                    action.type.startsWith("appointment/") &&
                    action.type.endsWith("/fulfilled"),
                (state) => {
                    state.loading = false;
                }
            )

            .addMatcher(
                (action) =>
                    action.type.startsWith("appointment/") &&
                    action.type.endsWith("/rejected"),
                (state) => {
                    state.loading = false;
                }
            );
    },
});

export const {
    setSelectedAppointment,
    unsetSelectedAppointment,
    resetStatus,
} = appointmentSlice.actions;

export default appointmentSlice.reducer;